// ─────────────────────────────────────────────────────────────────────────────
// Hedge Matrix Column ID Utility
//
// Single source of truth for building, parsing, and migrating column IDs.
//
// Naming convention:  {type}:{silo}:...rest
//   - segment[0] = column type (strike, otm, pricing, hedge, pnl, qty, spacer)
//   - segment[1] = silo (portfolio underlying ticker, e.g. SPX, ES_H26)
//   - remaining segments carry type-specific data
//
// Group IDs follow the same convention and always end with :group
// ─────────────────────────────────────────────────────────────────────────────

// ── Column ID Builders ──────────────────────────────────────────────────────

export function buildStrikeColId(silo: string): string {
    return `strike:${silo}`;
}

export function buildOtmColId(silo: string): string {
    return `otm:${silo}`;
}

export function buildPricingColId(silo: string, exp: string, side: string): string {
    return `pricing:${silo}:${exp}:${side}`;
}

export function buildHedgeColId(silo: string, hedgeId: string, type: 'qty' | 'trans' | 'outcome'): string {
    return `hedge:${silo}:${hedgeId}:${type}`;
}

export function buildPnlExpirationColId(silo: string, exp: string, side: string, allInclusive?: boolean): string {
    const base = `pnl:expiration:${silo}:${exp}:${side}`;
    return allInclusive ? `${base}:all` : base;
}

export function buildPnlTotalColId(silo: string, exp: string): string {
    return `pnl:total:${silo}:${exp}`;
}

export function buildQtyTotalColId(silo: string, side: string): string {
    return `qty:total:${silo}:${side}`;
}

export function buildSpacerColId(n: number): string {
    return `spacer:${n}`;
}

// ── Group ID Builders ───────────────────────────────────────────────────────

export function buildPinnedGroupId(): string {
    return 'pinned:group';
}

export function buildHedgeGroupId(silo: string, hedgeId: string): string {
    return `hedge:${silo}:${hedgeId}:group`;
}

export function buildQtyTotalGroupId(silo: string, side: string): string {
    return `qty:total:${silo}:${side}:group`;
}

export function buildPnlTotalGroupId(silo: string, exp: string): string {
    return `pnl:total:${silo}:${exp}:group`;
}

export function buildPnlExpirationGroupId(silo: string, exp: string, side: string, allInclusive?: boolean): string {
    const base = `pnl:expiration:${silo}:${exp}:${side}`;
    return allInclusive ? `${base}:all:group` : `${base}:group`;
}

export function buildSectionGroupId(silo: string): string {
    return `section:${silo}:group`;
}

// ── Column Type Constants ───────────────────────────────────────────────────

export type ColumnType =
    | 'strike'
    | 'otm'
    | 'pricing'
    | 'hedge'
    | 'pnl:expiration'
    | 'pnl:total'
    | 'qty:total'
    | 'spacer'
    | 'section'
    | 'pinned'
    | 'unknown';

// ── Parsed Column ID ────────────────────────────────────────────────────────

export interface ParsedColId {
    type: ColumnType;
    silo?: string;
    exp?: string;
    side?: string;
    hedgeId?: string;
    hedgeSubType?: 'qty' | 'trans' | 'outcome';
    allInclusive?: boolean;
    spacerIndex?: number;
    raw: string;
}

// ── Parsers ─────────────────────────────────────────────────────────────────

export function parseColId(colId: string): ParsedColId {
    const raw = colId;
    const segments = colId.split(':');
    const prefix = segments[0];

    // strike:{silo}
    if (prefix === 'strike') {
        return { type: 'strike', silo: segments[1], raw };
    }

    // otm:{silo}
    if (prefix === 'otm') {
        return { type: 'otm', silo: segments[1], raw };
    }

    // pricing:{silo}:{exp}:{side}
    if (prefix === 'pricing') {
        return { type: 'pricing', silo: segments[1], exp: segments[2], side: segments[3], raw };
    }

    // hedge:{silo}:{hedgeId}:{qty|trans|outcome|group}
    if (prefix === 'hedge') {
        const subType = segments[3] as 'qty' | 'trans' | 'outcome';
        return { type: 'hedge', silo: segments[1], hedgeId: segments[2], hedgeSubType: subType, raw };
    }

    // pnl:expiration:{silo}:{exp}:{side}[:all][:group]
    // pnl:total:{silo}:{exp}[:group]
    if (prefix === 'pnl') {
        if (segments[1] === 'expiration') {
            const allInclusive = segments.includes('all');
            return {
                type: 'pnl:expiration',
                silo: segments[2],
                exp: segments[3],
                side: segments[4],
                allInclusive,
                raw
            };
        }
        if (segments[1] === 'total') {
            return { type: 'pnl:total', silo: segments[2], exp: segments[3], raw };
        }
    }

    // qty:total:{silo}:{side}[:group]
    if (prefix === 'qty' && segments[1] === 'total') {
        return { type: 'qty:total', silo: segments[2], side: segments[3], raw };
    }

    // spacer:{n}
    if (prefix === 'spacer') {
        return { type: 'spacer', spacerIndex: parseInt(segments[1], 10), raw };
    }

    // section:{silo}:group
    if (prefix === 'section') {
        return { type: 'section', silo: segments[1], raw };
    }

    // pinned:group
    if (prefix === 'pinned') {
        return { type: 'pinned', raw };
    }

    return { type: 'unknown', raw };
}

export function getColumnType(colId: string): ColumnType {
    return parseColId(colId).type;
}

export function getSilo(colId: string): string | undefined {
    return parseColId(colId).silo;
}

// ── Type Guards ─────────────────────────────────────────────────────────────

export function isStrikeColumn(colId: string): boolean {
    return colId.startsWith('strike:');
}

export function isOtmColumn(colId: string): boolean {
    return colId.startsWith('otm:');
}

export function isHedgeColumn(colId: string): boolean {
    return colId.startsWith('hedge:');
}

export function isPricingColumn(colId: string): boolean {
    return colId.startsWith('pricing:');
}

export function isPnlExpirationColumn(colId: string): boolean {
    return colId.startsWith('pnl:expiration:');
}

export function isPnlTotalColumn(colId: string): boolean {
    return colId.startsWith('pnl:total:');
}

export function isPnlColumn(colId: string): boolean {
    return colId.startsWith('pnl:');
}

export function isQtyTotalColumn(colId: string): boolean {
    return colId.startsWith('qty:total:');
}

export function isSpacerColumn(colId: string): boolean {
    return colId.startsWith('spacer:');
}

export function isGroupId(id: string): boolean {
    return id.endsWith(':group');
}

export function isPinnedColumn(colId: string): boolean {
    return isStrikeColumn(colId) || isOtmColumn(colId);
}

export function isStructuralColumn(colId: string): boolean {
    return isPinnedColumn(colId) || isSpacerColumn(colId) || isPricingColumn(colId);
}

// ── Extractors ──────────────────────────────────────────────────────────────

export function getHedgeIdFromColId(colId: string): string | undefined {
    if (!isHedgeColumn(colId)) return undefined;
    return colId.split(':')[2];
}

// ── Migration ───────────────────────────────────────────────────────────────

const NEW_FORMAT_PREFIXES = [
    'strike:', 'otm:', 'pricing:', 'hedge:',
    'pnl:', 'qty:', 'spacer:', 'pinned:', 'section:'
];

function isNewFormat(id: string): boolean {
    return NEW_FORMAT_PREFIXES.some(p => id.startsWith(p));
}

/**
 * Converts an old-format column/group ID to the new format.
 *
 * @param oldId            The column or group ID to migrate
 * @param resolveHedgeSilo Callback that resolves a hedgeId to its silo (underlying ticker)
 * @param baseSilo         The base portfolio underlying (e.g. 'SPX')
 * @param esUnderlyings    List of known ES underlying tickers for disambiguation
 */
export function migrateColId(
    oldId: string,
    resolveHedgeSilo: (hedgeId: string) => string,
    baseSilo: string,
    esUnderlyings: string[] = []
): string {
    // Idempotency: already new format
    if (isNewFormat(oldId)) return oldId;

    // ── Static IDs ──
    if (oldId === 'strike') return buildStrikeColId(baseSilo);
    if (oldId === 'otm') return buildOtmColId(baseSilo);
    if (oldId === 'pinned') return buildPinnedGroupId();

    // ── Spacer: spacerN → spacer:N ──
    const spacerMatch = oldId.match(/^spacer(\d+)$/);
    if (spacerMatch) return buildSpacerColId(parseInt(spacerMatch[1], 10));

    // ── Determine if this is an ES-prefixed ID ──
    let esPrefix: string | null = null;
    let remainder = oldId;
    for (const ul of esUnderlyings) {
        if (oldId.startsWith(ul + ':')) {
            esPrefix = ul;
            remainder = oldId.substring(ul.length + 1);
            break;
        }
    }

    const silo = esPrefix || baseSilo;

    // ── ES strike: {ul}:strike → strike:{ul} ──
    if (remainder === 'strike') {
        return buildStrikeColId(silo);
    }

    // ── ES section: {ul}:section → section:{ul}:group ──
    if (remainder === 'section') {
        return buildSectionGroupId(silo);
    }

    // ── P&L expiration patterns ──
    // {exp}:pnl-expiration:{side}:all:group
    // {exp}:pnl-expiration:{side}:all
    // {exp}:pnl-expiration:{side}:group  (note: older format had :group without :all before :group)
    // {exp}:pnl-expiration:{side}
    const pnlExpMatch = remainder.match(/^(.+):pnl-expiration:(Call|Put)(?::(all))?(?::(group))?$/);
    if (pnlExpMatch) {
        const [, exp, side, allFlag, groupFlag] = pnlExpMatch;
        const allInclusive = allFlag === 'all';
        if (groupFlag === 'group') {
            return buildPnlExpirationGroupId(silo, exp, side, allInclusive);
        }
        return buildPnlExpirationColId(silo, exp, side, allInclusive);
    }

    // ── Grand total P&L: {exp}:grandtotalpnl[:group] ──
    const grandTotalMatch = remainder.match(/^(.+):grandtotalpnl(?::(group))?$/);
    if (grandTotalMatch) {
        const [, exp, groupFlag] = grandTotalMatch;
        return groupFlag === 'group'
            ? buildPnlTotalGroupId(silo, exp)
            : buildPnlTotalColId(silo, exp);
    }

    // ── Expiration pricing: {exp}:expiration:{side} ──
    const expPricingMatch = remainder.match(/^(.+):expiration:(Call|Put)$/);
    if (expPricingMatch) {
        const [, exp, side] = expPricingMatch;
        return buildPricingColId(silo, exp, side);
    }

    // ── Qty total: {side}:total[:group] ──
    const totalMatch = remainder.match(/^(Call|Put):total(?::(group))?$/);
    if (totalMatch) {
        const [, side, groupFlag] = totalMatch;
        return groupFlag === 'group'
            ? buildQtyTotalGroupId(silo, side)
            : buildQtyTotalColId(silo, side);
    }

    // ── Hedge colgroup: {hedgeId}:colgroup ──
    const colgroupMatch = remainder.match(/^(.+):colgroup$/);
    if (colgroupMatch) {
        const hedgeId = colgroupMatch[1];
        return buildHedgeGroupId(resolveHedgeSilo(hedgeId), hedgeId);
    }

    // ── Hedge trans: {hedgeId}:trans ──
    const transMatch = remainder.match(/^(.+):trans$/);
    if (transMatch) {
        const hedgeId = transMatch[1];
        return buildHedgeColId(resolveHedgeSilo(hedgeId), hedgeId, 'trans');
    }

    // ── Hedge outcome: {hedgeId}:outcome ──
    const outcomeMatch = remainder.match(/^(.+):outcome$/);
    if (outcomeMatch) {
        const hedgeId = outcomeMatch[1];
        return buildHedgeColId(resolveHedgeSilo(hedgeId), hedgeId, 'outcome');
    }

    // ── Bare hedgeId (no colons remaining) ──
    if (!remainder.includes(':')) {
        return buildHedgeColId(resolveHedgeSilo(remainder), remainder, 'qty');
    }

    // ── Unknown pattern — pass through with warning ──
    console.warn('[HM-ColId] migrateColId: unrecognized ID pattern, passing through unchanged:', oldId);
    return oldId;
}

/**
 * Migrates an entire saved GridState from old-format IDs to new-format.
 */
export function migrateGridState(
    state: { colState?: any[]; groupState?: any[]; expirationPnlNormalizedState?: any[] },
    resolveHedgeSilo: (hedgeId: string) => string,
    baseSilo: string,
    esUnderlyings: string[] = []
): { colState?: any[]; groupState?: any[]; expirationPnlNormalizedState?: any[] } {
    if (!state) return state;

    const migrated: any = { ...state };

    if (state.colState) {
        migrated.colState = state.colState.map(col => {
            const newColId = migrateColId(col.colId, resolveHedgeSilo, baseSilo, esUnderlyings);
            return newColId !== col.colId ? { ...col, colId: newColId } : col;
        });
    }

    if (state.groupState) {
        migrated.groupState = state.groupState.map(grp => {
            const newGroupId = migrateColId(grp.groupId, resolveHedgeSilo, baseSilo, esUnderlyings);
            return newGroupId !== grp.groupId ? { ...grp, groupId: newGroupId } : grp;
        });
    }

    // expirationPnlNormalizedState stores raw expiration strings, not column IDs — no migration needed

    return migrated;
}
